import { useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { Field, TextInput, TextArea, SelectInput } from "@/components/setup/SetupField";
import { StepAssets } from "@/components/setup/StepAssets";
import { CCHBC_MARKETS } from "@/components/setup/StepMarket";
import { useLang } from "@/i18n";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = { country: "", contact_name: "", contact_email: "", note: "" };

export const AssetDelivery = () => {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(null);

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors((e) => {
      const next = { ...e };
      Object.keys(patch).forEach((k) => delete next[k]);
      return next;
    });
  };

  const submit = async () => {
    const errs = {};
    if (!form.country) errs.country = "Please select your market";
    if (!form.contact_name.trim()) errs.contact_name = "Required";
    if (!EMAIL_RE.test(form.contact_email)) errs.contact_email = "Valid email required";
    if (files.length === 0) errs.files = "Please upload at least one file";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`${API}/api/asset-deliveries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, asset_files: files, portal_origin: window.location.origin }),
      });
      if (!res.ok) throw new Error(`Submission failed (${res.status})`);
      const data = await res.json();
      setSubmitted(data);
    } catch (e) {
      setSubmitError(e.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white border border-[#e8e8e8] rounded-sm p-10 sm:p-14 text-center max-w-2xl mx-auto" data-testid="asset-delivery-success">
        <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-7 h-7 text-white" strokeWidth={2} />
        </div>
        <span className="cchbc-eyebrow justify-center">{t("Assets received")}</span>
        <h2 className="font-condensed text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#000000] mt-3 mb-3">
          {t(submitted.country)} — {submitted.asset_files.length} {submitted.asset_files.length === 1 ? t("file delivered") : t("files delivered")}
        </h2>
        <p className="text-sm text-[#555555] leading-relaxed max-w-md mx-auto">
          {t("The Master F&B team will pick up your assets and confirm with")} <b>{submitted.contact_email}</b> {t("if anything else is needed.")}
        </p>
        <button
          onClick={() => {
            setSubmitted(null);
            setFiles([]);
            setForm((f) => ({ ...f, note: "" }));
          }}
          className="cchbc-btn cchbc-btn-primary mt-7"
          data-testid="asset-delivery-again-btn"
        >
          {t("Send more assets")}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="asset-delivery-form">
      <div className="bg-white border border-[#e8e8e8] rounded-sm p-6 sm:p-8 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <Field label="Country / Market" required error={errors.country} testId="delivery-field-country">
            <SelectInput
              options={CCHBC_MARKETS}
              placeholder="Select your market…"
              value={form.country}
              error={errors.country}
              onChange={(e) => update({ country: e.target.value })}
              data-testid="delivery-input-country"
            />
          </Field>
          <Field label="Your name" required error={errors.contact_name} testId="delivery-field-name">
            <TextInput
              value={form.contact_name}
              error={errors.contact_name}
              onChange={(e) => update({ contact_name: e.target.value })}
              placeholder="e.g. Maxim Sendrea"
              data-testid="delivery-input-name"
            />
          </Field>
          <Field label="Your email" required error={errors.contact_email} testId="delivery-field-email">
            <TextInput
              type="email"
              value={form.contact_email}
              error={errors.contact_email}
              onChange={(e) => update({ contact_email: e.target.value })}
              placeholder="name@cchellenic.com"
              data-testid="delivery-input-email"
            />
          </Field>
        </div>

        <Field label="What's in this delivery?" hint="Optional — e.g. 'Updated summer campaign banners + new packshots'" testId="delivery-field-note">
          <TextArea
            value={form.note}
            onChange={(e) => update({ note: e.target.value })}
            placeholder="Optional"
            data-testid="delivery-input-note"
          />
        </Field>

        <StepAssets
          showModeHint={false}
          files={files}
          onAdd={(meta) => setFiles((f) => [...f, meta])}
          onRemove={(fileId) => setFiles((f) => f.filter((x) => x.file_id !== fileId))}
        />
        {errors.files && (
          <p className="text-[11px] text-[#dd260e] font-semibold" data-testid="delivery-files-error">
            {t(errors.files)}
          </p>
        )}
      </div>

      {submitError && (
        <p className="text-sm text-[#dd260e] font-semibold mt-4" data-testid="delivery-submit-error">
          {submitError}
        </p>
      )}

      <div className="flex justify-end mt-6">
        <button onClick={submit} disabled={submitting} className="cchbc-btn cchbc-btn-primary" data-testid="delivery-submit-btn">
          <Send className="w-3.5 h-3.5" />
          {submitting ? t("Sending…") : t("Send Assets")}
        </button>
      </div>
    </div>
  );
};
